import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { handleApiErrors } from 'helpers';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useForm } from 'react-hook-form';
import { TaskMeasureUnitCardProps } from 'types/props/TaskMeasureUnitCardProps';

import { MeasureUnitSchema } from 'constants/schemas/MeasureUnitSchema';

import { MeasureUnitUI, RoleUI } from '../types/interfaces';

import '../ui/css/EmployeeCard.css';
import { CustomDropdown } from './custom-controls';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';
import { withDialogFooterSpacing } from './shared/dialogFooterSpacing';
import { TagsComponent } from './TagsComponent';
interface RefInterface {
  handleCancel: () => void;
}

const TaskMeasureUnitCard = React.forwardRef<RefInterface, TaskMeasureUnitCardProps>(
  (
    { taskMeasureUnitCardProps, onAdd, onUpdate, onDelete, closeDialog }: TaskMeasureUnitCardProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      setError,
      formState: { isDirty, errors }
    } = useForm({
      resolver: zodResolver(MeasureUnitSchema),
      defaultValues: {
        ...taskMeasureUnitCardProps
      }
    });
    const name = watch('name');
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const toast = useRef<Toast>(null);
    const businessProcessId = watch('id');

    const onSubmit: SubmitHandler<MeasureUnitUI> = useCallback(
      async (data) => {
        const newData = { ...data };
        const dataId = data.id;
        let result;
        const isIdExist =
          data && dataId !== undefined && dataId !== null && dataId !== 0;
        if (onUpdate && isIdExist) {
          result = await onUpdate(dataId, newData);
        }
        if (onAdd && !isIdExist) {
          newData.id = 0;
          result = await onAdd(newData);
        }

        if (result?.data.isSuccess) {
          closeDialog?.();
        } else {
          handleApiErrors({
            error: result?.data.error,
            setError,
            fieldMapping: {
              SameName: {
                field: 'name',
                message:
                  'Запись с таким названием уже существует. Пожалуйста, укажите уникальное название.'
              },
              
              SameShortName: {
                field: 'shortName',
                message:
                  'Запись с таким кортким названием уже существует. Пожалуйста, укажите уникальное название'
              }
            }
          });

          if (!result?.data.error) {
            toast.current?.show({
              severity: 'error',
              summary: 'Ошибка сохранения',
              detail: 'Не удалось сохранить единицу измерения'
            });
          }
        }
      },
      [closeDialog, onAdd, onUpdate, setError]
    );

    const onError = (errors: any) => {
      console.log('Ошибки валидации:', errors);
      if (toast.current) {
        toast.current.show({
          severity: 'error',
          summary: 'Ошибка валидации',
          detail: 'Проверьте заполнение полей',
          life: 3000
        });
      }
    };


    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      } else if (closeDialog) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });


    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
    };

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(businessProcessId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    return (
      <div className="dialog-tabless-card measureUnit-card__roles" style={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Toast ref={toast} />
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden'
        }}>
          <Divider />
          <div style={{ 
            flex: 1,
            display: 'grid',
            gridTemplateRows: 'auto auto auto 1fr auto', 
            minHeight: 0,
            overflow: 'hidden'
          }}>

            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="name" className="col p-0">
                    Название<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('name')}
                    />
                  </div>
                </div>
                <p
                  className={`task-status-card-errors card-errors ${
                    errors.name ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.name ? errors.name.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="shortName" className="col p-0">
                    Короткое название<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('shortName')}
                    />
                  </div>
                </div>
                <p
                  className={`task-status-card-errors card-errors ${
                    errors.shortName ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.shortName ? errors.shortName.message : ' '}
                </p>
              </div>
            </div>


            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="description" className="col p-0">
                    Описание:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('description')}
                    />
                  </div>
                </div>
                <p
                  className={`task-status-card-errors card-errors ${
                    errors.description ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.description ? errors.description.message : ' '}
                </p>
              </div>
            </div>

            <div>
              <div className="flex gap-3 default-card__buttons">
                <Button
                  label="Сохранить"
                  icon="pi pi-check"
                  onClick={handleSubmit(onSubmit, onError)}
                />
                <Button
                  label="Удалить"
                  icon="pi pi-trash"
                  onClick={handleDelete}
                  className="p-button-danger"
                />
                <Button
                  label="Отмена"
                  icon="pi pi-times"
                  onClick={handleCancel}
                  className="p-button-secondary"
                />
              </div>
            </div>
          </div>
        </div>
        <Dialog
          visible={showDialog}
          onHide={handleConfirmationCancel}
          header="Подтверждение"
          pt={withDialogFooterSpacing()}
          footer={
            <div>
              <Button label="Продолжить" onClick={handleConfirmationContinue} />
              <Button
                label="Отмена"
                onClick={handleConfirmationCancel}
                className="p-button-secondary"
              />
            </div>
          }
        >
          <div>Есть несохраненные изменения. Вы хотите продолжить?</div>
        </Dialog>

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="единицу измерения"
          entityName={name}
        />

      </div>
    );
  }
);

TaskMeasureUnitCard.displayName = 'TaskMeasureUnitCard';

export default TaskMeasureUnitCard;
